/**
 * Dispute Detail Page
 * Display detailed dispute information and admin decision form
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Link,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Avatar,
  Breadcrumbs,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Description as DocumentIcon,
  Launch as LaunchIcon,
} from '@mui/icons-material';
import { MainLayout } from '../../../components/layout/MainLayout';
import { LoadingSpinner, EmptyState } from '../../../components/common';
import { DisputeService } from '../../../services/dispute/DisputeService';
import type { DisputeDetail, DisputeDecision } from '../../../types/dispute';
import { formatDateTime } from '../../../utils/dateUtils';
import {
  Card,
  CardContent,
  TextField,
  PrimaryButton,
} from '../../../components/ui';
import { useMessage } from '../../../contexts/MessageContext';
import { StatusTimelineCard } from './components/StatusTimelineCard';

const DisputeDetailPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { showSuccess, showError } = useMessage();
  const { id } = useParams<{ id: string }>();
  const [dispute, setDispute] = useState<DisputeDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [decision, setDecision] = useState<DisputeDecision | ''>('');
  const [decisionReason, setDecisionReason] = useState('');
  const [refundAmount, setRefundAmount] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isResolved = dispute?.status === 'resolved' || dispute?.status === 'rejected';
  const isWaitingForMoreInfo = dispute?.decision === 'request_more_info';
  const canSubmitDecision = !isResolved && !isWaitingForMoreInfo;

  useEffect(() => {
    const fetchDisputeDetail = async () => {
      if (!id) {
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        const detail = await DisputeService.getDisputeDetail(id);
        setDispute(detail);
        if (detail?.decision) {
          setDecision(detail.decision);
        }
        if (detail?.decisionReason) {
          setDecisionReason(detail.decisionReason);
        }
        if (detail?.refundAmount) {
          setRefundAmount(detail.refundAmount.toString());
        }
      } catch (error) {
        console.error('Failed to fetch dispute detail:', error);
        setDispute(null);
      } finally {
        setIsLoading(false);
      }
    };

    fetchDisputeDetail();
  }, [id]);

  const handleSubmitDecision = async () => {
    if (!dispute || !decision) {
      showError('Please select a decision.');
      return;
    }

    if (!decisionReason.trim()) {
      showError('Please provide a reason for your decision.');
      return;
    }

    // Validate partial refund amount
    if (decision === 'partial_refund') {
      const amount = parseFloat(refundAmount);
      if (isNaN(amount) || amount <= 0) {
        showError('Refund amount must be greater than 0.');
        return;
      }
      if (dispute.disputedAmount && amount > dispute.disputedAmount) {
        showError(`Refund amount cannot exceed disputed amount ($${dispute.disputedAmount.toFixed(2)}).`);
        return;
      }
    }

    try {
      setIsSubmitting(true);
      // Determine refund amount based on decision type
      let refundAmountValue: number | undefined;
      if (decision === 'partial_refund') {
        refundAmountValue = parseFloat(refundAmount);
      } else if (decision === 'approve_refund') {
        refundAmountValue = dispute.disputedAmount;
      }
      await DisputeService.submitDecision(
        dispute.id,
        decision as DisputeDecision,
        decisionReason,
        refundAmountValue
      );
      // Refresh dispute data
      const detail = await DisputeService.getDisputeDetail(dispute.id);
      setDispute(detail);
      // Update local state with refreshed data
      if (detail?.decision) {
        setDecision(detail.decision);
      }
      if (detail?.decisionReason) {
        setDecisionReason(detail.decisionReason);
      }
      if (detail?.refundAmount) {
        setRefundAmount(detail.refundAmount.toString());
      }
      showSuccess('Decision submitted successfully.');
    } catch (error) {
      console.error('Failed to submit decision:', error);
      showError(error instanceof Error ? error.message : 'Failed to submit decision.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusColor = (status: string, theme: any): string => {
    switch (status) {
      case 'underreview':
        return theme.palette.warning.main;
      case 'resolved':
        return theme.palette.success.main;
      case 'rejected':
        return theme.palette.error.main;
      default:
        return theme.palette.text.secondary;
    }
  };

  const getStatusLabel = (status: string): string => {
    switch (status) {
      case 'underreview':
        return 'Under Review';
      case 'resolved':
        return 'Resolved';
      case 'rejected':
        return 'Rejected';
      default:
        return status;
    }
  };


  if (isLoading) {
    return (
      <MainLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', padding: theme.spacing(8) }}>
          <LoadingSpinner />
        </Box>
      </MainLayout>
    );
  }

  if (!dispute) {
    return (
      <MainLayout>
        <EmptyState
          title="Dispute Not Found"
          message="The dispute you're looking for doesn't exist or has been removed."
        />
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      {/* Dispute Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: theme.spacing(3),
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(2) }}>
          <Typography
            variant="h4"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
            }}
          >
            Mission Title: {dispute.missionTitle}
          </Typography>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              padding: theme.spacing(0.5, 1.5),
              borderRadius: theme.shapes.borderRadius.lg,
              backgroundColor: getStatusColor(dispute.status, theme),
              color: theme.palette.common.white,
              fontWeight: 500,
              fontSize: '0.875rem',
            }}
          >
            {getStatusLabel(dispute.status)}
          </Box>
        </Box>
      </Box>

      {/* Created Date */}
      {dispute.createdAt && (
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.text.secondary,
            marginBottom: theme.spacing(3),
          }}
        >
          Created on: {formatDateTime(dispute.createdAt)}
        </Typography>
      )}

      {/* Main Content */}
      <Grid container spacing={3}>
        {/* Left Column - Main Content */}
        <Grid item xs={12} md={8}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(3) }}>
            {/* Dispute Summary */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: theme.spacing(2),
                    color: theme.palette.text.primary,
                    fontSize: '1rem',
                  }}
                >
                  Dispute Summary
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: theme.typography.caption.fontSize,
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      Reason:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        fontSize: theme.typography.body2.fontSize,
                      }}
                    >
                      {dispute.reason || 'N/A'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.secondary,
                        fontSize: theme.typography.caption.fontSize,
                        marginBottom: theme.spacing(0.5),
                      }}
                    >
                      Mission Transaction Amount:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: theme.palette.text.primary,
                        fontWeight: 500,
                        fontSize: theme.typography.body2.fontSize,
                      }}
                    >
                      ${dispute.disputedAmount?.toFixed(2) || '0.00'}
                    </Typography>
                  </Grid>
                  {dispute.initiatorName && (
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.caption.fontSize,
                          marginBottom: theme.spacing(1),
                        }}
                      >
                        Initiated By:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: theme.spacing(1) }}>
                        <Typography
                          variant="body2"
                          sx={{
                            color: theme.palette.text.primary,
                            fontWeight: 500,
                            fontSize: theme.typography.body2.fontSize,
                          }}
                        >
                          {dispute.initiatorName}
                        </Typography>
                        <Box
                          component="span"
                          sx={{
                            display: 'inline-block',
                            width: 'fit-content',
                            padding: theme.spacing(0.5, 1.5),
                            borderRadius: theme.shapes.borderRadius.md,
                            backgroundColor: dispute.initiatorType === 'asset'
                              ? theme.palette.primary.main
                              : theme.palette.secondary.main,
                            color: theme.palette.common.white,
                            fontWeight: 600,
                            fontSize: theme.typography.caption.fontSize,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                          }}
                        >
                          {dispute.initiatorType === 'asset' ? 'Asset' : 'Ghost'}
                        </Box>
                      </Box>
                    </Grid>
                  )}
                  {dispute.missionId && (
                    <Grid item xs={12} sm={6}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.caption.fontSize,
                          marginBottom: theme.spacing(0.5),
                        }}
                      >
                        Related Mission:
                      </Typography>
                      <Link
                        component="button"
                        onClick={() => {
                          navigate(`/missions/${dispute.missionId}`);
                        }}
                        sx={{
                          color: theme.palette.primary.main,
                          textDecoration: 'none',
                          fontWeight: 500,
                          fontSize: theme.typography.body2.fontSize,
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          gap: theme.spacing(0.5),
                          '&:hover': {
                            textDecoration: 'underline',
                          },
                        }}
                      >
                        {dispute.missionCode}
                        <LaunchIcon sx={{ fontSize: 14 }} />
                      </Link>
                    </Grid>
                  )}
                  {(dispute.cancellationReason || dispute.reason) && (
                    <Grid item xs={12}>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.secondary,
                          fontSize: theme.typography.caption.fontSize,
                          marginBottom: theme.spacing(0.5),
                        }}
                      >
                        User's Claim:
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: theme.typography.body2.fontSize,
                          lineHeight: 1.6,
                          padding: theme.spacing(1.5),
                          backgroundColor: theme.customColors.searchBackground,
                          borderRadius: 1,
                        }}
                      >
                        {dispute.cancellationReason || dispute.reason}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>

            {/* Mission Evidence - Evidence from the related mission (dispute filing party upload not supported yet) */}
            <Card>
              <CardContent>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    marginBottom: theme.spacing(2),
                    color: theme.palette.text.primary,
                    fontSize: theme.typography.body1.fontSize,
                  }}
                >
                  Mission Evidence
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: 'repeat(2, 1fr)',
                      sm: 'repeat(3, 1fr)',
                    },
                    gap: theme.spacing(2),
                  }}
                >
                  {dispute.evidence && dispute.evidence.length > 0 ?
                    (dispute.evidence.map((item) => (
                      <Box
                        key={item.id}
                        sx={{
                          position: 'relative',
                          borderRadius: '4px',
                          overflow: 'hidden',
                          border: `1px solid ${theme.customColors.tableBorder}`,
                          backgroundColor: theme.customColors.searchBackground,
                          cursor: 'pointer',
                          '&:hover': {
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                        onClick={() => {
                          window.open(item.fileUrl, '_blank');
                        }}
                      >
                        {item.fileType === 'image' ? (
                          <img
                            src={item.fileUrl}
                            alt={item.fileName}
                            style={{
                              width: '100%',
                              height: 'auto',
                              display: 'block',
                            }}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.src = `https://via.placeholder.com/200x150/CCCCCC/666666?text=${encodeURIComponent(item.fileName)}`;
                            }}
                          />
                        ) : (
                          <Box
                            sx={{
                              padding: theme.spacing(3),
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              justifyContent: 'center',
                              minHeight: 120,
                            }}
                          >
                            <DocumentIcon sx={{ fontSize: 48, color: theme.palette.text.secondary }} />
                          </Box>
                        )}
                        <Box
                          sx={{
                            position: 'absolute',
                            bottom: 0,
                            left: 0,
                            right: 0,
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            padding: theme.spacing(0.75),
                          }}
                        >
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.common.white,
                              fontSize: theme.typography.caption.fontSize,
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.fileName}
                          </Typography>
                        </Box>
                      </Box>
                    ))) :
                    (
                      <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                        No evidence available
                      </Typography>
                    )
                  }
                </Box>
              </CardContent>
            </Card>
            {/* Communication History */}
            {dispute.communicationHistory && dispute.communicationHistory.length > 0 && (
              <Card>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      marginBottom: theme.spacing(2),
                      color: theme.palette.text.primary,
                      fontSize: theme.typography.body1.fontSize,
                    }}
                  >
                    Communication History
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: theme.spacing(2) }}>
                    {dispute.communicationHistory.map((message) => (
                      <Box
                        key={message.id}
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          gap: theme.spacing(1.5),
                          justifyContent: message.sender === 'asset' ? 'flex-start' : 'flex-end',
                        }}
                      >
                        {message.sender === 'asset' && (
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: theme.palette.grey[600],
                              fontSize: theme.typography.body2.fontSize,
                            }}
                          >
                            {message.senderName.charAt(0)}
                          </Avatar>
                        )}
                        <Box
                          sx={{
                            flex: 1,
                            maxWidth: '70%',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: theme.spacing(0.5),
                          }}
                        >
                          <Box
                            sx={{
                              padding: theme.spacing(1.5, 2),
                              borderRadius: theme.shapes.borderRadius.md,
                              backgroundColor:
                                message.sender === 'asset'
                                  ? theme.customColors.searchBackground
                                  : theme.palette.primary.main,
                              color:
                                message.sender === 'asset'
                                  ? theme.palette.text.primary
                                  : theme.palette.primary.contrastText,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontSize: theme.typography.body2.fontSize,
                                lineHeight: 1.5,
                              }}
                            >
                              {message.message}
                            </Typography>
                          </Box>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: theme.typography.caption.fontSize,
                              alignSelf: message.sender === 'asset' ? 'flex-start' : 'flex-end',
                            }}
                          >
                            {message.senderName} - {formatDateTime(message.timestamp)}
                          </Typography>
                        </Box>
                        {message.sender === 'ghost' && (
                          <Avatar
                            sx={{
                              width: 32,
                              height: 32,
                              backgroundColor: theme.palette.primary.main,
                              fontSize: theme.typography.body2.fontSize,
                            }}
                          >
                            {message.senderName.charAt(0)}
                          </Avatar>
                        )}
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* More Information Submitted by User */}
            {dispute.additionalInformation && (
              <Card sx={{ marginBottom: theme.spacing(3) }}>
                <CardContent>
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 600,
                      marginBottom: theme.spacing(2.5),
                      color: theme.palette.text.primary,
                      fontSize: '1rem',
                    }}
                  >
                    More Information
                  </Typography>

                  <Box sx={{ position: 'relative' }}>
                    {/* Content */}
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: theme.typography.body2.fontSize,
                          lineHeight: 1.6,
                          padding: theme.spacing(1.5),
                          backgroundColor: theme.customColors.searchBackground,
                          borderRadius: 1,
                        }}
                      >
                        {dispute.additionalInformation}
                      </Typography>

                      {dispute.additionalInformationProvidedAt && (
                        <Box sx={{ marginTop: theme.spacing(0.5) }}>
                          <Typography
                            variant="caption"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontSize: '0.75rem',
                            }}
                          >
                            {formatDateTime(dispute.additionalInformationProvidedAt)}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Mission Timeline */}
            {dispute.taskLogs && dispute.taskLogs.length > 0 && (
              <StatusTimelineCard statusHistory={dispute.taskLogs} />
            )}
          </Box>
        </Grid>

        {/* Right Column - Admin Decision */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  marginBottom: theme.spacing(2),
                  color: theme.palette.text.primary,
                  fontSize: '1rem',
                }}
              >
                Admin Decision
              </Typography>
              {isWaitingForMoreInfo ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.warning.main,
                      fontSize: '0.875rem',
                      marginBottom: theme.spacing(2),
                      fontWeight: 500,
                    }}
                  >
                    Waiting for more information from the user.
                  </Typography>
                  <Box sx={{ marginBottom: theme.spacing(2) }}>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, display: 'block', marginBottom: theme.spacing(0.5) }}
                    >
                      Decision
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Request More Info
                    </Typography>
                  </Box>
                  {dispute.decisionReason && (
                    <Box sx={{ marginBottom: theme.spacing(2) }}>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary, display: 'block', marginBottom: theme.spacing(0.5) }}
                      >
                        Request Note
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {dispute.decisionReason}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : isResolved ? (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      marginBottom: theme.spacing(2),
                    }}
                  >
                    This dispute has been processed. View the decision below.
                  </Typography>
                  <Box sx={{ marginBottom: theme.spacing(2) }}>
                    <Typography
                      variant="caption"
                      sx={{ color: theme.palette.text.secondary, display: 'block', marginBottom: theme.spacing(0.5) }}
                    >
                      Decision
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {getStatusLabel(dispute.status)} {dispute.decision && `(${dispute.decision.replace(/_/g, ' ')})`}
                    </Typography>
                  </Box>
                  {dispute.decisionReason && (
                    <Box sx={{ marginBottom: theme.spacing(2) }}>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary, display: 'block', marginBottom: theme.spacing(0.5) }}
                      >
                        Reason
                      </Typography>
                      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                        {dispute.decisionReason}
                      </Typography>
                    </Box>
                  )}
                  {dispute.refundAmount != null && dispute.refundAmount > 0 && (
                    <Box>
                      <Typography
                        variant="caption"
                        sx={{ color: theme.palette.text.secondary, display: 'block', marginBottom: theme.spacing(0.5) }}
                      >
                        Refund Amount
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        ${dispute.refundAmount.toFixed(2)}
                      </Typography>
                    </Box>
                  )}
                </>
              ) : (
                <>
                  <Typography
                    variant="body2"
                    sx={{
                      color: theme.palette.text.secondary,
                      fontSize: '0.875rem',
                      marginBottom: theme.spacing(3),
                    }}
                  >
                    Select an action and provide a reason for the decision.
                  </Typography>

                  <FormControl component="fieldset" sx={{ width: '100%', marginBottom: theme.spacing(3) }} disabled={!canSubmitDecision}>
                    <RadioGroup
                      value={decision}
                      onChange={(e) => {
                        const newDecision = e.target.value as DisputeDecision;
                        setDecision(newDecision);
                        // Clear refund amount if not partial refund
                        if (newDecision !== 'partial_refund') {
                          setRefundAmount('');
                        }
                      }}
                    >
                      <FormControlLabel
                        value="approve_refund"
                        control={<Radio />}
                        label="Approve Refund"
                        sx={{
                          marginBottom: theme.spacing(1),
                          '& .MuiFormControlLabel-label': {
                            color: theme.palette.text.primary,
                            fontSize: theme.typography.body2.fontSize,
                          },
                        }}
                      />
                      <FormControlLabel
                        value="partial_refund"
                        control={<Radio />}
                        label="Partial Refund"
                        sx={{
                          marginBottom: theme.spacing(1),
                          '& .MuiFormControlLabel-label': {
                            color: theme.palette.text.primary,
                            fontSize: theme.typography.body2.fontSize,
                          },
                        }}
                      />
                      <FormControlLabel
                        value="reject_refund"
                        control={<Radio />}
                        label="Reject Refund"
                        sx={{
                          marginBottom: theme.spacing(1),
                          '& .MuiFormControlLabel-label': {
                            color: theme.palette.text.primary,
                            fontSize: theme.typography.body2.fontSize,
                          },
                        }}
                      />
                      <FormControlLabel
                        value="request_more_info"
                        control={<Radio />}
                        label="Request More Info"
                        sx={{
                          '& .MuiFormControlLabel-label': {
                            color: theme.palette.text.primary,
                            fontSize: theme.typography.body2.fontSize,
                          },
                        }}
                      />
                    </RadioGroup>
                  </FormControl>

                  {/* Partial Refund Amount Input */}
                  {decision === 'partial_refund' && (
                    <FormControl fullWidth sx={{ marginBottom: theme.spacing(3) }}>
                      <FormLabel
                        sx={{
                          color: theme.palette.text.primary,
                          fontSize: theme.typography.body2.fontSize,
                          marginBottom: theme.spacing(1),
                          fontWeight: 500,
                        }}
                      >
                        Refund Amount (Mandatory)
                      </FormLabel>
                      <TextField
                        type="number"
                        placeholder={`Max: $${dispute.disputedAmount?.toFixed(2) || '0.00'}`}
                        value={refundAmount}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Allow empty string for clearing, or valid number
                          if (value === '' || (!isNaN(parseFloat(value)) && parseFloat(value) >= 0)) {
                            setRefundAmount(value);
                          }
                        }}
                        inputProps={{
                          min: 0,
                          max: dispute.disputedAmount || undefined,
                          step: 0.01,
                        }}
                        required
                        disabled={!canSubmitDecision}
                        error={
                          refundAmount !== '' &&
                          (isNaN(parseFloat(refundAmount)) ||
                            parseFloat(refundAmount) <= 0 ||
                            (dispute.disputedAmount !== undefined &&
                              parseFloat(refundAmount) > dispute.disputedAmount))
                        }
                        helperText={
                          refundAmount !== '' &&
                          (isNaN(parseFloat(refundAmount)) || parseFloat(refundAmount) <= 0
                            ? 'Amount must be greater than 0'
                            : dispute.disputedAmount !== undefined &&
                              parseFloat(refundAmount) > dispute.disputedAmount
                              ? `Amount cannot exceed disputed amount ($${dispute.disputedAmount.toFixed(2)})`
                              : '')
                        }
                        sx={{
                          '& .MuiFormHelperText-root': {
                            color: theme.palette.error.main,
                            fontSize: theme.typography.caption.fontSize,
                          },
                        }}
                      />
                      {dispute.disputedAmount && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: theme.palette.text.secondary,
                            fontSize: theme.typography.caption.fontSize,
                            marginTop: theme.spacing(0.5),
                          }}
                        >
                          Disputed Amount: ${dispute.disputedAmount.toFixed(2)}
                        </Typography>
                      )}
                    </FormControl>
                  )}

                  <FormControl fullWidth sx={{ marginBottom: theme.spacing(3) }}>
                    <FormLabel
                      sx={{
                        color: theme.palette.text.primary,
                        fontSize: theme.typography.body2.fontSize,
                        marginBottom: theme.spacing(1),
                        fontWeight: 500,
                      }}
                    >
                      Reason for Decision (Mandatory)
                    </FormLabel>
                    <TextField
                      multiline
                      rows={6}
                      placeholder="Provide a clear justification for your decision..."
                      value={decisionReason}
                      onChange={(e) => setDecisionReason(e.target.value)}
                      required
                      disabled={!canSubmitDecision}
                    />
                  </FormControl>

                  <PrimaryButton
                    fullWidth
                    onClick={handleSubmitDecision}
                    disabled={
                      !canSubmitDecision ||
                      !decision ||
                      !decisionReason.trim() ||
                      isSubmitting ||
                      (decision === 'partial_refund' &&
                        (refundAmount === '' ||
                          isNaN(parseFloat(refundAmount)) ||
                          parseFloat(refundAmount) <= 0 ||
                          (dispute.disputedAmount !== undefined &&
                            parseFloat(refundAmount) > dispute.disputedAmount)))
                    }
                    sx={{
                      padding: theme.spacing(1.5),
                      '&:disabled': {
                        backgroundColor: theme.palette.action.disabledBackground,
                        color: theme.palette.action.disabled,
                      },
                    }}
                  >
                    Submit Decision
                  </PrimaryButton>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </MainLayout>
  );
};

export default DisputeDetailPage;

